import {regNavn} from "felles/tjenester/tredjepartsTjenestenavn";
import {IModule, IRootScopeService} from "angular";
import {l} from "types/typescriptdefinisjoner_lederplan";
import moment from "moment";
import _ from "lodash";
import {gettextCatalog} from "angular-gettext";
import {MøteRessurs} from "felles/tjenester/ressurser/MøteRessurs";
import {GruppeRessurs} from "felles/tjenester/ressurser/GruppeRessurs";
import {MalmøteRessurs} from "felles/tjenester/ressurser/MalmøteRessurs";
import {Datoer} from "felles/tjenester/Datoer";
import {UuidGenerator} from "felles/tjenester/UuidGenerator";
import {Kontekst} from "felles/tjenester/Kontekst";
import {Debugger} from "debug";
import {RedigerEksternMøtedeltakerModell} from "./komponenter/møteskjema/redigerEksternDeltakerSkjemalinje";
import {RedigerEksterneDeltakereKontroller} from "./komponenter/møteskjema/redigerEksterneDeltakere";
import {MøtedeltakerSkjemaModell, VelgMøtedeltakereSkjemaModell} from "./komponenter/nyttMøteSkjema/nyttMøteVelgDeltakereSkjema";
import {PubliseringSpesifikasjon} from "./komponenter/nyttMøteSkjema/nyttMøtePubliseringSkjema";
import {MeldingTilBruker, Varighet} from "felles/tjenester/MeldingTilBruker";
import {WebNavigasjon} from "web/tjenester/WebNavigasjon";
import {MøteDatatjenesteForAjs, MøtedialogerForAjs} from "web/nedgraderteNg2Tjenester";
import {GruppeId, MøteId} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {Temporal} from "temporal-polyfill";
import {Steg} from "web-ng2/moduler/møte/modale/møte-rediger-dialog/møte-rediger-dialog";
import {ng2RegNavn} from "web/nedgraderteNg2TjenesterRegisternavn";
import Gruppetype = l.Gruppetype;
import TilgangsrolleMedlem = l.TilgangsrolleMedlem;
import PlainDate = Temporal.PlainDate;

const debug: Debugger = require('debug')('MøteTjeneste');

export class MøteTjeneste {

    static regNavn = "wm.møte.MøteTjeneste";

    static registrerTjenester(angular: IModule) {
        angular
            .service(MøteTjeneste.regNavn, MøteTjeneste.forInjisering());
    }

    static forInjisering() {
        return [
            regNavn.ng.$rootScope,
            MøteRessurs.regNavn,
            MeldingTilBruker.regNavn,
            WebNavigasjon.regNavn,
            regNavn.gettext.gettextCatalog,
            ng2RegNavn.MøteDatatjenesteForAjs,
            ng2RegNavn.MøtedialogerForAjs,
            MøteTjeneste
        ]
    }

    constructor(private $rootScope: IRootScopeService,
                private møteRes: MøteRessurs,
                private meldingTilBruker: MeldingTilBruker,
                private navigasjon: WebNavigasjon,
                private gettextCatalog: gettextCatalog,
                private møteDatatjenesteForAjs: MøteDatatjenesteForAjs,
                private møtedialogerForAjs:MøtedialogerForAjs) {
    }

    nyttMøteDialog(valgtGruppeId: GruppeId | null = null, valgtStartdato: PlainDate | null = null){
        this.møtedialogerForAjs.tjenestekallOmsluttetIAjsApplyAsync(this.$rootScope, tjeneste =>
          tjeneste.visOpprett(valgtGruppeId, valgtStartdato));
    }

    redigerMøteDialog(møteId: MøteId, initieltSteg = Steg.detaljer) {
        this.møtedialogerForAjs.tjenestekallOmsluttetIAjsApplyAsync(this.$rootScope, tjeneste =>
          tjeneste.visRediger(møteId, initieltSteg));
    }

    publiserMøtet(møte: l.MøteModell) {
        this.møtedialogerForAjs.tjenestekallOmsluttetIAjsApplyAsync(this.$rootScope, tjeneste =>
          tjeneste.visPubliserMøte(møte.id));
    }

    opphevPubliseringenAvMøtet(møteId:string) {
        this.møteRes.publiserMøte(møteId, {erPublisert: false}).$promise
            .then(valideringsresultat => this.håndterMøtePubliseringValideringsfeil(valideringsresultat, møteId));
    }

    sendInnkalling(møte: l.MøteModell) {
        this.møtedialogerForAjs.tjenestekallOmsluttetIAjsApplyAsync(this.$rootScope, tjeneste =>
          tjeneste.visSendInnkalling(møte.id));
    }

    publiserProtokoll(møte: l.MøteModell) {
        this.møtedialogerForAjs.tjenestekallOmsluttetIAjsApplyAsync(this.$rootScope, tjeneste =>
          tjeneste.visPubliserProtokoll(møte.id));
    }

    opphevPubliseringenAvProtokollen(møteId: string) {
        this.møteDatatjenesteForAjs.tjeneste().then(møteDatatjeneste =>
          møteDatatjeneste.oppdaterProtokollPublisering(møteId, {erPublisert: false}));
    }

    sendProtokollTilSignering(møte: l.MøteModell){
        this.møtedialogerForAjs.tjenestekallOmsluttetIAjsApplyAsync(this.$rootScope, tjeneste =>
          tjeneste.visSendSignerProtokollInvitasjon(møte.id));
    }

    startTranskripsjon(møte: l.MøteModell) {
        this.møtedialogerForAjs.tjenestekallOmsluttetIAjsApplyAsync(this.$rootScope, tjeneste =>
          tjeneste.visStartTranskripsjon(møte.id));
    }

    signerProtokoll(møteId: string) {
        this.navigasjon.gåTilSignerMøteProtokoll(møteId, false);
    }

    /** Mulige verdier for l.InkludertGenerertDokument enum med label */
    inkluderGenerertDokumentValg(): { [verdi: number]: string } {
        const valg = <any>{};
        /// inkluderGenerertDokument dropdown på malsak - ingen dok skal legges til
        valg[l.InkludertGenerertDokument.ingen] = this.gettextCatalog.getString('ingen');
        /// inkluderGenerertDokument dropdown på malsak - agenda dok skal legges til
        valg[l.InkludertGenerertDokument.agenda] = this.gettextCatalog.getString('Agenda for dette møte');
        /// inkluderGenerertDokument dropdown på malsak - protokoll dok skal legges til
        valg[l.InkludertGenerertDokument.protokoll] = this.gettextCatalog.getString('Protokoll fra forrige møte');
        /// inkluderGenerertDokument dropdown på malsak - forfalte oppgaver dok skal legges til
        valg[l.InkludertGenerertDokument.forfalteOppgaver] = this.gettextCatalog.getString('Forfalte oppgaver');

        return valg;
    }

    // private hjelpemetoder

    /** håndterer event. valideringsfeil, returnerer usann dersom ingen feil, sann dersom angitt valideringsresultat inneholde valideringsfeil */
    private håndterMøtePubliseringValideringsfeil(valideringsresultat: l.ValideringsresultatModell<l.MøtePubliseringValideringsfeil>, møteId:string){
        if (!valideringsresultat.harFeil) {
            //sørger for å relaste møte
            this.møteDatatjenesteForAjs.tjeneste().then(møteDatatjeneste =>
              møteDatatjeneste.relast(møteId));
            return false;
        }

        switch (valideringsresultat.feil[0].feilkode){
            case l.MøtePubliseringValideringsfeil.åpent_for_avstemning:
                return this.meldingTilBruker.feilmelding('Møtet er åpent for avstemning, du kan derfor ikke oppheve møte-pulisering', Varighet.sideskift, () => {this.navigasjon.gåTilMøteAvstemning(møteId); return true;}, false)
            case l.MøtePubliseringValideringsfeil.protokoll_låst: //den kan normalt ikke nås da "opphev publisering" knappen ikke vises dersom protokoll er låst
                return this.meldingTilBruker.feilmelding('Protokoll fra møte er låst, du kan derfor ikke oppheve møte-pulisering', Varighet.sideskift, null, false)
        }
        return true;
    }

}

export class NyttMøteSkjemaModell implements VelgMøtedeltakereSkjemaModell {
    spesifikasjon: l.NyttMøteSpesifikasjon & PubliseringSpesifikasjon;
    tilgjengeligeOrdindaereGrupper: l.GruppeModell[];
    tilgjengeligeMalGrupper: l.GruppeModell[];
    gruppe: l.GruppeModell;
    moetedato: l.MøteDato;
    standardStarttidspunkt: string;
    standardVarighet: number;
    tillattStartdatoPeriode: l.PlanPeriode;
    sendInnkalling: boolean;
    moetemal: l.MalmøteModell;
    kopiAvEksisterendeMøte:l.MøteModell = null; //kun satt dersom denne NyttMøteSkjemaModell instans representerer en kopiering av et eksisterende møte

    //VelgMøtedeltakereSkjemaModell impl
    deltakere: MøtedeltakerSkjemaModell[];
    eksterneDeltakere: RedigerEksternMøtedeltakerModell[];

    constructor(møtedato:Date|null) {
        debug("NyttMøteSkjemaModell.constructor", møtedato)
        this.spesifikasjon = {
            id: null, //settes via uuidGenerator.generer() når uuidGenerator er tilgjengelig
            behandlingsSted: '',
            behandlingsMaate: '',
            formaal: '',
            valgteMalsaker: [],
            //følgende blir populert ved lagring
            gruppeId: null,
            tittel: null,
            dato: {start: null, slutt: null},
            deltakere: [],
            deltakereSomSkalSignereProtokoll: [],
            eksterneDeltakere: [],
            erMoetePublisert: true,
            erProtokollPublisert: true,
        }
        this.tillattStartdatoPeriode = {start: null, slutt: null};
        this.moetedato = {start: null, slutt:null};
        if(!_.isNil(møtedato))
            this.moetedato.start = møtedato;
        this.eksterneDeltakere = [];

        //eksterne data
        this.deltakere = [];
        this.moetemal = null; //blir satt når grupper har lastet
        this.gruppe = null; //blir satt når grupper har lastet
        this.tilgjengeligeOrdindaereGrupper = [];
        this.tilgjengeligeMalGrupper = [];
    }

    static nyNyttMøteSkjemaModell(gruppeId:string|null, møtedato:Date, gruppeRes: GruppeRessurs, malmøteRes: MalmøteRessurs, uuidGenerator: UuidGenerator, kontekst:Kontekst): NyttMøteSkjemaModell {
        debug("nyNyttMøteSkjemaModell", møtedato)
        var modell = new NyttMøteSkjemaModell(møtedato);
        modell.spesifikasjon.id = uuidGenerator.generer();

        //last eksterne data
        gruppeRes.finnTilgjengeligeGrupper().$promise
            .then(grupper => {
                const medSkrivetilgang = grupper.filter(gruppe => kontekst.inneharTilgangsrolleIGruppe(TilgangsrolleMedlem.skrive, gruppe.id));
                modell.tilgjengeligeOrdindaereGrupper = medSkrivetilgang.filter(gruppe => gruppe.gruppetype == Gruppetype.ordinær);
                modell.tilgjengeligeMalGrupper = medSkrivetilgang.filter(gruppe => gruppe.gruppetype == Gruppetype.mal);
                modell.gruppe = _(medSkrivetilgang).find(g => g.id == gruppeId);
                //dersom bruker kun har tilgang til én enkelt ordinær gruppe velger vi denne som gruppe per default
                if (!modell.gruppe && modell.tilgjengeligeOrdindaereGrupper.length == 1) modell.gruppe = modell.tilgjengeligeOrdindaereGrupper[0];
                if (modell.gruppe) modell.lastMøtemalOgOppdaterModell(malmøteRes);
            });

        return modell;
    }

    static nyNyttMøteSkjemaModellFraMøtemal(møtedato:Date, mal: l.MalmøteModell, gruppe: l.GruppeModell, uuidGenerator: UuidGenerator): NyttMøteSkjemaModell {
        debug("nyNyttMøteSkjemaModellFraMøtemal", møtedato, mal)
        var modell = new NyttMøteSkjemaModell(møtedato);
        modell.spesifikasjon.id = uuidGenerator.generer();
        modell.gruppe = gruppe;
        modell.tilgjengeligeOrdindaereGrupper = [gruppe];
        modell.oppdaterFraMøtemal(mal);
        return modell;
    }

    static nyNyttMøteSkjemaModellFraMøte(møtedato:Date, mal: l.MøteModell, gruppe: l.GruppeModell, uuidGenerator: UuidGenerator): NyttMøteSkjemaModell {
        debug("nyNyttMøteSkjemaModellFraMøte", mal)
        const malmøte = this.møteSomMalmøte(mal);
        const nyttMøteSkjemaModell = this.nyNyttMøteSkjemaModellFraMøtemal(møtedato, malmøte, gruppe, uuidGenerator);
        nyttMøteSkjemaModell.kopiAvEksisterendeMøte = mal;
        nyttMøteSkjemaModell.spesifikasjon.formaal = mal.formaal;
        return nyttMøteSkjemaModell;
    }

    kopier():NyttMøteSkjemaModell{
        const kopi = new NyttMøteSkjemaModell(this.moetedato.start);
        kopi.spesifikasjon = _.cloneDeep(this.spesifikasjon);
        kopi.tilgjengeligeOrdindaereGrupper = this.tilgjengeligeOrdindaereGrupper;
        kopi.tilgjengeligeMalGrupper = this.tilgjengeligeMalGrupper;
        kopi.gruppe = this.gruppe;
        kopi.moetedato = _.clone(this.moetedato);
        kopi.standardStarttidspunkt = this.standardStarttidspunkt;
        kopi.standardVarighet = this.standardVarighet;
        kopi.tillattStartdatoPeriode = this.tillattStartdatoPeriode;
        kopi.deltakere = _.clone(this.deltakere);
        kopi.eksterneDeltakere = _.cloneDeep(this.eksterneDeltakere);
        kopi.moetemal = this.moetemal;
        kopi.sendInnkalling = this.sendInnkalling;
        kopi.moetemal = this.moetemal;
        kopi.kopiAvEksisterendeMøte = this.kopiAvEksisterendeMøte;

        return kopi;
    }

    lastMøtemalOgOppdaterModell(malmøteRes: MalmøteRessurs, utelatteFelter: NyttMøteSkjemaFelter[] = []) {
        this.spesifikasjon.valgteMalsaker = [];
        malmøteRes.hent(this.gruppe.id).$promise
            .then(mal => this.oppdaterFraMøtemal(mal, utelatteFelter));
    }

    /** Oppdaterer modellen med data fra angitt mal.
     * Merk at felter inkludert i utelatteFelter ikke vil bli oppdatert fra mal. */
    oppdaterFraMøtemal(mal: l.MalmøteModell, utelatteFelter: NyttMøteSkjemaFelter[] = []) {
        this.moetemal = mal;
        if (!_.includes(utelatteFelter, NyttMøteSkjemaFelter.starttidspunkt))
            this.standardStarttidspunkt = mal.starttidspunkt;
        if (!_.includes(utelatteFelter, NyttMøteSkjemaFelter.varighet))
            this.standardVarighet = mal.varighet;
        if (!_.isNil(this.standardStarttidspunkt))
            this.moetedato.start = Datoer.datoMedKlokkeslett(this.moetedato.start, this.standardStarttidspunkt);
        if (!_.isNil(this.standardVarighet))
            this.moetedato.slutt = moment(this.moetedato.start).add("minutes", this.standardVarighet).toDate();
        this.moetedato.start
        this.spesifikasjon.valgteMalsaker = mal.saker.filter(sak => sak.standardIMoete).map(sak => sak.id);
        this.deltakere = this.gruppe.medlemmer.map(medlem => ({medlem: medlem, skalDelta: false, skalSignereProtokoll: false}));
        this.deltakere.forEach(deltaker => {
            const deltakerFraMal = _.find(mal.deltakere, d => d.medlem.id == deltaker.medlem.id);
            //mal.deltakere inneholder medlemmer som skal delta - dersom deltakerkandidat ikke finnes her skal vedkommende ikke delta
            if (_.isNil(deltakerFraMal)) {
                deltaker.skalDelta = false;
                return;
            }
            deltaker.skalDelta = true;
            deltaker.skalSignereProtokoll = deltakerFraMal.skalSignere;
        });
        if (!_.includes(utelatteFelter, NyttMøteSkjemaFelter.behandlingsSted))
            this.spesifikasjon.behandlingsSted = mal.behandlingsSted;
        if (!_.includes(utelatteFelter, NyttMøteSkjemaFelter.behandlingsmaate))
            this.spesifikasjon.behandlingsMaate = mal.behandlingsMaate;
        this.spesifikasjon.erMoetePublisert = mal.erMoetePublisert;
        this.spesifikasjon.erProtokollPublisert = mal.erProtokollPublisert;
    }

    somNyttMøteSpesifikasjon(): l.NyttMøteSpesifikasjon {
        return {
            id: this.spesifikasjon.id,
            gruppeId: this.gruppe.id,
            tittel: this.gruppe.moetenavn,
            dato: {start: this.moetedato.start, slutt: this.moetedato.slutt},
            formaal: this.spesifikasjon.formaal,
            behandlingsSted: this.spesifikasjon.behandlingsSted,
            behandlingsMaate: this.spesifikasjon.behandlingsMaate,
            deltakere: this.deltakereSomSkalDelta().map(deltaker => ({medlemId: deltaker.medlem.id, medlemInvitasjon: null, representerteAksjeeiere: null})),
            deltakereSomSkalSignereProtokoll: this.deltakereSomSkalDelta().filter(deltaker => deltaker.skalSignereProtokoll).map(deltaker => deltaker.medlem.id),
            eksterneDeltakere: RedigerEksterneDeltakereKontroller.spesifikasjoner(this.eksterneDeltakere),
            valgteMalsaker: this.spesifikasjon.valgteMalsaker,
            erMoetePublisert: this.spesifikasjon.erMoetePublisert,
            erProtokollPublisert: this.spesifikasjon.erProtokollPublisert
        }
    }

    somKopierMøteSpesifikasjon(): l.KopierMøteSpesifikasjon {
        return {
            id: this.spesifikasjon.id,
            kopiAvMoeteId: this.kopiAvEksisterendeMøte.id,
            gruppeId: this.gruppe.id,
            tittel: this.gruppe.moetenavn,
            dato: {start: this.moetedato.start, slutt: this.moetedato.slutt},
            formaal: this.spesifikasjon.formaal,
            behandlingsSted: this.spesifikasjon.behandlingsSted,
            behandlingsMaate: this.spesifikasjon.behandlingsMaate,
            deltakere: this.deltakereSomSkalDelta().map(deltaker => deltaker.medlem.id),
            deltakereSomSkalSignereProtokoll: this.deltakereSomSkalDelta().filter(deltaker => deltaker.skalSignereProtokoll).map(deltaker => deltaker.medlem.id),
            eksterneDeltakere: RedigerEksterneDeltakereKontroller.spesifikasjoner(this.eksterneDeltakere),
            valgteSaker: this.spesifikasjon.valgteMalsaker,
            erMoetePublisert: this.spesifikasjon.erMoetePublisert,
            erProtokollPublisert: this.spesifikasjon.erProtokollPublisert
        };
    }

    private deltakereSomSkalDelta(){
        return this.deltakere.filter(deltaker => deltaker.skalDelta);
    }

    private static møteSomMalmøte(møte: l.MøteModell):l.MalmøteModell{
        return {
            id: null,
            eiendeGruppeId: møte.eiendeGruppeId,
            tittel: møte.tittel,
            starttidspunkt: moment(møte.dato.start).format("HH:mm"),
            varighet: MøteRessurs.møtevarighet(møte),
            behandlingsSted: møte.behandlingsSted,
            behandlingsMaate: møte.behandlingsMaate,
            erMoetePublisert: møte.tilgang.erMoetePublisert,
            erProtokollPublisert: møte.tilgang.erProtokollPublisert,
            saker: møte.saker.map(sak => this.sakSomMalsak(sak)),
            deltakere: møte.deltakere.map(deltaker => this.deltakerSomMalmøteDeltaker(deltaker))
        };
    }

    private static sakSomMalsak(sak:l.AgendaelementModell):l.MalsakModell{
        return {
            id: sak.id, //obs - bruker her et AgendaelementModell.id og ikke et MalsakModell.id
            eiendeGruppeId: sak.eiendeGruppeId,
            tittel: sak.tittel,
            beskrivelse: sak.beskrivelse,
            varighet: sak.varighet,
            tema: sak.tema,
            sakstype: sak.sakstype,
            ansvarlig: sak.ansvarlig,
            inkludertGenerertDokument: null,  //OpprettPlanFabrikk.java sørger for å kopierer dokumenter
            beslutningsforslagtittel: '', //OpprettPlanFabrikk.java sørger for å kopierer beslutninger
            beslutningsforslagbeskrivelse: '',  //OpprettPlanFabrikk.java sørger for å kopierer beslutninger
            erPause: sak.erPause,
            standardIMoete: true
        };
    }

    private static deltakerSomMalmøteDeltaker(deltaker:l.MøtedeltakerModell):l.MalmøteDeltakerModell{
        return {
            medlem: deltaker.medlem,
            skalSignere: false //OpprettPlanFabrikk.java sørger for å benytte det som er satt på møtemalmedlem
        };
    }
}

export enum NyttMøteSkjemaFelter {
    starttidspunkt,
    varighet,
    behandlingsSted,
    behandlingsmaate
}

export class OppdaterMøteSkjemaModell {
    spesifikasjon: l.OppdaterMøteSpesifikasjon;
    gruppe: l.GruppeModell;
    moetedato: l.MøteDato;
    deltakere: l.GruppeMedlemListeModell[];
    tilgjengeligeDeltakere: l.GruppeMedlemListeModell[];
    eksterneDeltakere: RedigerEksternMøtedeltakerModell[];
    opprinneligeDeltakere: l.MøtedeltakerModell[];
    sendInnkalling: boolean = false;

    static nyRedigerMøteSkjemaModell(møte: l.MøteModell, gruppeRes: GruppeRessurs): OppdaterMøteSkjemaModell {
        var modell = new OppdaterMøteSkjemaModell();
        modell.spesifikasjon = {
            id: møte.id,
            tittel: møte.tittel,
            behandlingsSted: møte.behandlingsSted,
            behandlingsMaate: møte.behandlingsMaate,
            formaal: møte.formaal,
            //følgende blir populert ved lagring
            dato: {start: null, slutt: null},
            deltakere: [],
            eksterneDeltakere: []
        };

        modell.moetedato = {start: møte.dato.start, slutt: møte.dato.slutt};
        modell.deltakere = _(møte.deltakere).map(d => d.medlem).value();
        modell.eksterneDeltakere = møte.eksterneDeltakere.map(d => new RedigerEksternMøtedeltakerModell(d));
        modell.opprinneligeDeltakere = møte.deltakere;

        //last eksterne data
        gruppeRes.hent(møte.eiendeGruppeId).$promise
          .then(gruppe => modell.initGruppe(gruppe));
        return modell;
    }

    erNyeDeltakereLagtTil() {
        const opprinneligeDeltakerIder = _(this.opprinneligeDeltakere).map(d => d.medlem.id);
        return _(this.deltakere).some(d => !opprinneligeDeltakerIder.includes(d.id))
    }

    somOppdaterMøteSpesifikasjon() {
        this.spesifikasjon.dato = {start: this.moetedato.start, slutt: this.moetedato.slutt};
        this.spesifikasjon.deltakere = _(this.deltakere).map(deltaker => deltaker.id).value();
        this.spesifikasjon.eksterneDeltakere = RedigerEksterneDeltakereKontroller.spesifikasjoner(this.eksterneDeltakere);

        return this.spesifikasjon;
    }

    private initGruppe(gruppe: l.GruppeModell) {
        this.gruppe = gruppe;
        this.tilgjengeligeDeltakere = [...this.gruppe.medlemmer];
    }
}

export enum Møtevalideringsfeil {
    ok = 0, ingenPlan = 1, overlappendeMoete = 2, protokollLaast = 3, sluttFoerStart = 4
}



